import { create } from 'zustand';
import type { Character, CharacterSummary } from '@machina/core';
import { createCharacter, getCharacterSummary, XP_THRESHOLDS, applyLevelUp } from '@machina/core';
import { supabase, DbCharacter } from '../lib/supabase';
import { createSubscription } from '../lib/realtimeHelpers';
import { persistWithRetry, notifyPersistFailure } from '../lib/persistence';
import { notify } from './notificationStore';

/**
 * Character Store
 *
 * Manages player characters and NPCs for the campaign.
 * Syncs to Supabase for persistence.
 */

interface CharacterState {
  // Data
  characters: Map<string, Character>;
  partyIds: string[]; // IDs of characters in the active party
  characterOwners: Map<string, string>; // characterId → userId (DB user_id)

  // UI state
  selectedCharacterId: string | null;
  flyoutCharacterId: string | null;

  // Sync state
  isSyncing: boolean;
  lastSyncError: string | null;
  realtimeCleanup: (() => void) | null;
  currentCampaignId: string | null;
  currentUserId: string | null;

  // Actions
  addCharacter: (params: Parameters<typeof createCharacter>[0], campaignId?: string) => Character;
  updateCharacter: (id: string, updates: Partial<Character>) => void;
  removeCharacter: (id: string) => void;

  // Party management
  addToParty: (id: string) => void;
  removeFromParty: (id: string) => void;
  reorderParty: (fromIndex: number, toIndex: number) => void;

  // HP management
  damageCharacter: (id: string, amount: number, attackerId?: string, damageType?: string) => {
    finalDamage: number;
    newHp: number;
    died: boolean;
  } | null;
  healCharacter: (id: string, amount: number, sourceId?: string) => void;
  setTempHp: (id: string, amount: number) => void;

  // Badge
  setBadge: (id: string, badgeId: string) => void;
  setBadgeIcon: (id: string, badgeIcon: string) => void;

  // Session presence
  setAbsent: (id: string, absent: boolean) => void;

  // Level-up
  awardXp: (id: string, amount: number) => { leveledUp: boolean };
  levelUp: (id: string, choices: import('@machina/core').LevelUpChoices) => void;

  // UI
  selectCharacter: (id: string | null) => void;
  showFlyout: (id: string | null) => void;

  // Supabase sync
  loadFromSupabase: (userId: string, campaignId?: string) => Promise<void>;
  saveToSupabase: (userId: string, campaignId?: string) => Promise<void>;
  syncCharacterToSupabase: (characterId: string, userId: string, campaignId?: string) => Promise<void>;
  deleteFromSupabase: (characterId: string) => Promise<void>;

  // Realtime
  subscribeToRealtime: (campaignId: string) => void;
  unsubscribeFromRealtime: () => void;

  // Queries
  getCharacter: (id: string) => Character | undefined;
  getMyCharacterIds: () => string[]; // Characters owned by the current user
  getPartyMembers: () => Character[];
  getPartySummaries: () => CharacterSummary[];
  getActivePartyMembers: () => Character[]; // Excludes absent characters
  getActivePartySummaries: () => CharacterSummary[]; // Excludes absent characters
}

// Convert Character to DB format — character's own campaignId takes precedence
function characterToDb(char: Character, userId: string, campaignId?: string): Omit<DbCharacter, 'created_at' | 'updated_at'> {
  return {
    id: char.id,
    user_id: userId,
    campaign_id: char.campaignId || campaignId || null,
    data: char as unknown as Record<string, unknown>,
  };
}

// Convert DB format to Character — stamp campaignId from the DB row
function dbToCharacter(db: DbCharacter): Character {
  const char = db.data as unknown as Character;
  char.campaignId = db.campaign_id || undefined;
  return char;
}

// Seed iso sprite cache from a character's persisted isoSprites URLs.
// Called on initial load + realtime INSERT/UPDATE so sprites appear on every
// client without a reload when the GM generates + auto-saves.
function seedIsoSpritesFromChar(char: Character): void {
  const saved = char.isoSprites;
  if (!saved) return;
  // Dynamic import avoids circular module graph between character + iso stores.
  void import('./isoSpriteStore').then(({ useIsoSpriteStore }) => {
    const isoStore = useIsoSpriteStore.getState();
    for (const f of ['south', 'north', 'east', 'west'] as const) {
      const url = saved[f];
      if (url) isoStore.setCustomSprite(char.id, url, f, char.name);
    }
  }).catch((err) => console.warn('[characterStore] iso seed import failed:', err));
}

// Per-character debounced sync — each character has its own timer
const syncTimers = new Map<string, ReturnType<typeof setTimeout>>();

function debouncedSync(characterId: string, delayMs = 1000) {
  const existing = syncTimers.get(characterId);
  if (existing) clearTimeout(existing);

  const timer = setTimeout(() => {
    syncTimers.delete(characterId);
    const state = useCharacterStore.getState();
    const char = state.characters.get(characterId);
    if (!char?.campaignId || !state.currentUserId) return;
    state.syncCharacterToSupabase(characterId, state.currentUserId, char.campaignId);
  }, delayMs);

  syncTimers.set(characterId, timer);
}

export const useCharacterStore = create<CharacterState>()(
    (set, get) => ({
      characters: new Map(),
      partyIds: [],
      characterOwners: new Map(),
      selectedCharacterId: null,
      flyoutCharacterId: null,
      isSyncing: false,
      lastSyncError: null,
      realtimeCleanup: null,
      currentCampaignId: null,
      currentUserId: null,

      // =======================================================================
      // CRUD
      // =======================================================================

      addCharacter: (params, campaignId) => {
        const cid = campaignId || get().currentCampaignId || undefined;
        const character = createCharacter({ ...params, campaignId: cid });

        set((state) => {
          const newChars = new Map(state.characters);
          newChars.set(character.id, character);

          // Join the party immediately. Without this a freshly created
          // character sat in `characters` but not `partyIds`, so the left rail
          // showed them while the Initiative panel reported "No characters in
          // party" — until a refresh, at which point loadFromSupabase pushed
          // every campaign character in and it silently fixed itself.
          //
          // Unconditional on purpose: the load path and the realtime INSERT
          // handler both add every character regardless of isNpc, so filtering
          // here would make the party differ before and after a reload, which
          // is a worse bug than the one being fixed. Whether NPCs belong in
          // partyIds at all is a real question, but it has to be answered in
          // all three places at once.
          const newPartyIds = state.partyIds.includes(character.id)
            ? state.partyIds
            : [...state.partyIds, character.id];

          return { characters: newChars, partyIds: newPartyIds };
        });

        // Immediately sync new characters (not debounced — creation should persist right away)
        const uid = get().currentUserId;
        if (uid && character.campaignId) {
          get().syncCharacterToSupabase(character.id, uid, character.campaignId);
        }

        return character;
      },

      updateCharacter: (id, updates) => {
        set((state) => {
          const char = state.characters.get(id);
          if (!char) return state;

          const updated: Character = {
            ...char,
            ...updates,
            updatedAt: new Date(),
          };

          const newChars = new Map(state.characters);
          newChars.set(id, updated);
          return { characters: newChars };
        });
        debouncedSync(id);
      },

      removeCharacter: (id) => {
        set((state) => {
          const newChars = new Map(state.characters);
          newChars.delete(id);

          const newPartyIds = state.partyIds.filter((pid) => pid !== id);

          return {
            characters: newChars,
            partyIds: newPartyIds,
            selectedCharacterId:
              state.selectedCharacterId === id ? null : state.selectedCharacterId,
            flyoutCharacterId:
              state.flyoutCharacterId === id ? null : state.flyoutCharacterId,
          };
        });
      },

      // =======================================================================
      // PARTY
      // =======================================================================

      addToParty: (id) => {
        set((state) => {
          if (state.partyIds.includes(id)) return state;
          return { partyIds: [...state.partyIds, id] };
        });
      },

      removeFromParty: (id) => {
        set((state) => ({
          partyIds: state.partyIds.filter((pid) => pid !== id),
        }));
      },

      reorderParty: (fromIndex, toIndex) => {
        set((state) => {
          const newPartyIds = [...state.partyIds];
          const [removed] = newPartyIds.splice(fromIndex, 1);
          newPartyIds.splice(toIndex, 0, removed);
          return { partyIds: newPartyIds };
        });
      },

      // =======================================================================
      // HP
      // =======================================================================

      damageCharacter: (id, amount, _attackerId, _damageType) => {
        const char = get().characters.get(id);
        if (!char) return null;

        let remaining = amount;

        // Temp HP absorbs first
        let newTempHp = char.tempHp;
        if (newTempHp > 0) {
          const absorbed = Math.min(newTempHp, remaining);
          newTempHp -= absorbed;
          remaining -= absorbed;
        }

        // Then actual HP
        const newHp = Math.max(0, char.currentHp - remaining);
        const died = newHp === 0 && char.currentHp > 0;

        set((state) => {
          const updated: Character = {
            ...char,
            currentHp: newHp,
            tempHp: newTempHp,
            updatedAt: new Date(),
          };

          const newChars = new Map(state.characters);
          newChars.set(id, updated);
          return { characters: newChars };
        });
        debouncedSync(id);

        return { finalDamage: remaining, newHp, died };
      },

      healCharacter: (id, amount, _sourceId) => {
        set((state) => {
          const char = state.characters.get(id);
          if (!char) return state;

          const newHp = Math.min(char.maxHp, char.currentHp + amount);

          const updated: Character = {
            ...char,
            currentHp: newHp,
            updatedAt: new Date(),
          };

          const newChars = new Map(state.characters);
          newChars.set(id, updated);
          return { characters: newChars };
        });
        debouncedSync(id);
      },

      setTempHp: (id, amount) => {
        set((state) => {
          const char = state.characters.get(id);
          if (!char) return state;

          const updated: Character = {
            ...char,
            tempHp: Math.max(0, amount),
            updatedAt: new Date(),
          };

          const newChars = new Map(state.characters);
          newChars.set(id, updated);
          return { characters: newChars };
        });
        debouncedSync(id);
      },

      // =======================================================================
      // BADGE
      // =======================================================================

      setBadge: (id, badgeId) => {
        set((state) => {
          const char = state.characters.get(id);
          if (!char) return state;

          const updated: Character = {
            ...char,
            badgeId,
            updatedAt: new Date(),
          };

          const newChars = new Map(state.characters);
          newChars.set(id, updated);
          return { characters: newChars };
        });
        debouncedSync(id);
      },

      setBadgeIcon: (id, badgeIcon) => {
        set((state) => {
          const char = state.characters.get(id);
          if (!char) return state;

          const updated: Character = {
            ...char,
            badgeIcon,
            updatedAt: new Date(),
          };

          const newChars = new Map(state.characters);
          newChars.set(id, updated);
          return { characters: newChars };
        });
        debouncedSync(id);
      },

      // =======================================================================
      // SESSION PRESENCE
      // =======================================================================

      setAbsent: (id, absent) => {
        set((state) => {
          const char = state.characters.get(id);
          if (!char) return state;

          const updated: Character = {
            ...char,
            absentThisSession: absent,
            updatedAt: new Date(),
          };

          const newChars = new Map(state.characters);
          newChars.set(id, updated);
          return { characters: newChars };
        });
        debouncedSync(id);
      },

      // =======================================================================
      // LEVEL-UP
      // =======================================================================

      awardXp: (id, amount) => {
        const char = get().characters.get(id);
        if (!char) return { leveledUp: false };

        const newXp = (char.xp ?? 0) + amount;
        const totalLevel = char.classes.reduce((sum: number, c: { level: number }) => sum + c.level, 0);
        const nextThreshold = totalLevel < 20 ? XP_THRESHOLDS[totalLevel] : Infinity;
        const leveledUp = newXp >= nextThreshold;

        get().updateCharacter(id, {
          xp: newXp,
          pendingLevelUp: leveledUp,
        });

        return { leveledUp };
      },

      levelUp: (id, choices) => {
        const char = get().characters.get(id);
        if (!char) return;

        const updated = applyLevelUp(char, choices);

        // Apply all the level-up changes
        get().updateCharacter(id, updated);
      },

      // =======================================================================
      // UI
      // =======================================================================

      selectCharacter: (id) => set({ selectedCharacterId: id }),

      showFlyout: (id) => set({ flyoutCharacterId: id }),

      // =======================================================================
      // SUPABASE SYNC
      // =======================================================================

      loadFromSupabase: async (userId, campaignId) => {
        // Immediately clear and mark which campaign we're loading for
        set({
          characters: new Map(),
          partyIds: [],
          isSyncing: true,
          lastSyncError: null,
          currentCampaignId: campaignId || null,
          currentUserId: userId,
        });
        try {
          let query = supabase.from('machina_characters').select('*');

          if (campaignId) {
            // For campaign mode: load ALL characters in the campaign (shared party)
            query = query.eq('campaign_id', campaignId);
          } else {
            // No campaign: load only this user's personal characters
            query = query.eq('user_id', userId);
          }

          const { data, error } = await query;

          if (error) throw error;

          // Safety: if campaign changed while we were loading, discard results
          if (get().currentCampaignId !== (campaignId || null)) {
            console.log('[Characters] Campaign changed during load, discarding results');
            return;
          }

          if (data && data.length > 0) {
            const newChars = new Map<string, Character>();
            const newPartyIds: string[] = [];
            const newOwners = new Map<string, string>();

            for (const row of data) {
              const db = row as DbCharacter;
              const char = dbToCharacter(db);
              newChars.set(char.id, char);
              newPartyIds.push(char.id);
              if (db.user_id) {
                newOwners.set(char.id, db.user_id);
              }
            }

            // Seed iso sprite cache from persisted char.isoSprites —
            // so player sprites render immediately on any device without regen.
            try {
              const { useIsoSpriteStore } = await import('./isoSpriteStore');
              const isoStore = useIsoSpriteStore.getState();
              for (const char of newChars.values()) {
                const saved = char.isoSprites;
                if (!saved) continue;
                for (const f of ['south', 'north', 'east', 'west'] as const) {
                  const url = saved[f];
                  if (url) isoStore.setCustomSprite(char.id, url, f, char.name);
                }
              }
            } catch (err) {
              console.warn('[characterStore] iso sprite seed failed:', err);
            }

            set({
              characters: newChars,
              partyIds: newPartyIds,
              characterOwners: newOwners,
              isSyncing: false,
            });
          } else {
            // New campaign with no characters yet — clear the store
            // so old characters from a previous campaign don't bleed over
            set({
              characters: new Map(),
              partyIds: [],
              characterOwners: new Map(),
              isSyncing: false,
            });
          }
        } catch (err) {
          // A failed load leaves the party empty/stale with no other signal —
          // surface it so it's not silent, mirroring the save-failure UX.
          set({ lastSyncError: (err as Error).message, isSyncing: false });
          notifyPersistFailure('character load', err);
        }
      },

      saveToSupabase: async (userId, campaignId) => {
        // Guard: only save if characters are for the correct campaign
        const { currentCampaignId } = get();
        if (campaignId && currentCampaignId && campaignId !== currentCampaignId) {
          console.warn('[Characters] Refusing to save: campaign mismatch', { campaignId, currentCampaignId });
          return;
        }

        set({ isSyncing: true, lastSyncError: null });
        const { characters } = get();
        let allOk = true;

        for (const char of characters.values()) {
          const dbChar = characterToDb(char, userId, campaignId);
          const label = `${char.name || 'character'} sheet`;

          // persistWithRetry retries once and surfaces a sticky failure toast so
          // a bulk save can't fail silently. Record the error but keep saving the
          // rest of the party.
          const { ok, error } = await persistWithRetry(
            label,
            async () => await supabase.from('machina_characters').upsert(dbChar, { onConflict: 'id' }),
            {
              onFinalFailure: (err) => {
                set({ lastSyncError: err instanceof Error ? err.message : String(err) });
              },
            },
          );

          if (!ok) {
            allOk = false;
            void error; // already recorded via onFinalFailure
          }
        }

        set({ isSyncing: false, ...(allOk ? { lastSyncError: null } : {}) });
      },

      syncCharacterToSupabase: async (characterId, userId, campaignId) => {
        const char = get().characters.get(characterId);
        if (!char) return;

        // Guard: refuse to sync if character's campaignId doesn't match the target
        if (char.campaignId && campaignId && char.campaignId !== campaignId) {
          console.warn('[Characters] Refusing to sync: character campaign mismatch',
            { characterCampaignId: char.campaignId, targetCampaignId: campaignId });
          return;
        }

        const dbChar = characterToDb(char, userId, campaignId);
        const label = `${char.name || 'character'} sheet`;

        // Retry once before surfacing failure (same backoff as the shared
        // persistence helper). This hot path uses an inline loop rather than
        // persistWithRetry so the final-failure toast can carry an explicit
        // Retry action instead of the helper's plain sticky toast — a failed
        // HP / spell / inventory / level-up save is then NEVER lost silently.
        const upsert = () =>
          supabase.from('machina_characters').upsert(dbChar, { onConflict: 'id' });

        let lastError: unknown;
        for (let attempt = 0; attempt < 2; attempt++) {
          if (attempt > 0) {
            // Backoff before retrying — smooths over transient network blips.
            await new Promise((resolve) => setTimeout(resolve, 1500));
          }
          try {
            const { error } = await upsert();
            if (error) throw error;
            // Success — clear any stale failure marker.
            if (get().lastSyncError) set({ lastSyncError: null });
            return;
          } catch (err) {
            lastError = err;
            console.warn(
              `[Characters] Save of "${label}" failed (attempt ${attempt + 1}/2):`,
              err,
            );
          }
        }

        // Both attempts failed — surface a sticky toast with a Retry action and
        // record lastSyncError so callers / UI can react.
        set({ lastSyncError: lastError instanceof Error ? lastError.message : String(lastError) });
        notify({
          level: 'error',
          message: `Couldn't save ${label}`,
          detail: 'Recent HP, spell, inventory, or level-up changes may be lost on reload. Click retry.',
          ttl: 0, // sticky — user must dismiss
          action: {
            label: 'Retry',
            onClick: () => {
              void get().syncCharacterToSupabase(characterId, userId, campaignId);
            },
          },
        });
      },

      deleteFromSupabase: async (characterId) => {
        const name = get().characters.get(characterId)?.name;
        const label = `${name || 'character'} deletion`;

        const { ok, error } = await persistWithRetry(
          label,
          async () => await supabase.from('machina_characters').delete().eq('id', characterId),
          {
            onFinalFailure: (err) => {
              set({ lastSyncError: err instanceof Error ? err.message : String(err) });
            },
          },
        );

        if (!ok) {
          // persistWithRetry already showed the sticky failure toast; the extra
          // call here is a defensive no-op safeguard if the helper's toast path
          // changes — keeps deletion failures from ever going silent.
          set({ lastSyncError: error instanceof Error ? error.message : String(error) });
        }
      },

      // =======================================================================
      // REALTIME
      // =======================================================================

      subscribeToRealtime: (campaignId) => {
        get().unsubscribeFromRealtime();

        const { cleanup } = createSubscription({
          channelName: `characters:${campaignId}`,
          table: 'machina_characters',
          filter: `campaign_id=eq.${campaignId}`,
          event: '*',
          onPayload: (payload) => {
            const { characters, partyIds } = get();

            if (payload.eventType === 'INSERT') {
              const newChar = dbToCharacter(payload.new as unknown as DbCharacter);
              const newChars = new Map(characters);
              newChars.set(newChar.id, newChar);
              const newPartyIds = partyIds.includes(newChar.id)
                ? partyIds
                : [...partyIds, newChar.id];
              set({ characters: newChars, partyIds: newPartyIds });
              seedIsoSpritesFromChar(newChar);
            } else if (payload.eventType === 'UPDATE') {
              const updatedChar = dbToCharacter(payload.new as unknown as DbCharacter);
              const newChars = new Map(characters);
              newChars.set(updatedChar.id, updatedChar);
              const newPartyIds = partyIds.includes(updatedChar.id)
                ? partyIds
                : [...partyIds, updatedChar.id];
              set({ characters: newChars, partyIds: newPartyIds });
              // If the GM just auto-saved sprites, seed our local cache so the
              // sprite renders immediately without a reload.
              seedIsoSpritesFromChar(updatedChar);
            } else if (payload.eventType === 'DELETE') {
              const deletedId = (payload.old as { id: string }).id;
              const newChars = new Map(characters);
              newChars.delete(deletedId);
              const newPartyIds = partyIds.filter(id => id !== deletedId);
              set({ characters: newChars, partyIds: newPartyIds });
            }
          },
        });

        set({ realtimeCleanup: cleanup, currentCampaignId: campaignId });
      },

      unsubscribeFromRealtime: () => {
        const { realtimeCleanup } = get();
        if (realtimeCleanup) {
          realtimeCleanup();
          set({ realtimeCleanup: null });
        }
      },

      // =======================================================================
      // QUERIES
      // =======================================================================

      getCharacter: (id) => get().characters.get(id),

      getMyCharacterIds: () => {
        const { currentUserId, characterOwners, partyIds } = get();
        if (!currentUserId) return partyIds;
        return partyIds.filter((id) => characterOwners.get(id) === currentUserId);
      },

      getPartyMembers: () => {
        const { characters, partyIds } = get();
        return partyIds
          .map((id) => characters.get(id))
          .filter((c): c is Character => c !== undefined);
      },

      getPartySummaries: () => {
        const { characters, partyIds } = get();
        return partyIds
          .map((id) => characters.get(id))
          .filter((c): c is Character => c !== undefined)
          .map(getCharacterSummary);
      },

      getActivePartyMembers: () => {
        const { characters, partyIds } = get();
        return partyIds
          .map((id) => characters.get(id))
          .filter((c): c is Character => c !== undefined && !c.absentThisSession);
      },

      getActivePartySummaries: () => {
        const { characters, partyIds } = get();
        return partyIds
          .map((id) => characters.get(id))
          .filter((c): c is Character => c !== undefined && !c.absentThisSession)
          .map(getCharacterSummary);
      },
    })
);

// =============================================================================
// PARTY INITIALIZER - Lost Mines of Phandelver
// =============================================================================
//
// Party Metadata:
//   level: 5
//   tone: "competent adventurers; brave but not reckless; humor under pressure"
//   formation: ["Skolnit(point)", "Mad Juju(center)", "Bobby(anchor)", "Dax(support)"]
//

export function initializeDemoParty(campaignId?: string) {
  const store = useCharacterStore.getState();
  const existingNames = new Set(
    Array.from(store.characters.values()).map((c) => c.name)
  );

  // Dedup: only create characters that don't already exist by name
  const allPresent = ['Skolnit', 'Mad Juju', 'Bobby', 'Dax', 'Ashala', 'Brother Shago'].every((n) => existingNames.has(n));
  if (allPresent) return;

  const findByName = (name: string) =>
    Array.from(store.characters.values()).find((c) => c.name === name);

  // SKOLNIT - Gloom Stalker Ranger 5
  // Race: Standard Human | Background: Urchin
  // Role: Scout / Ambush / Ranged DPR
  const skolnit = findByName('Skolnit') || store.addCharacter({
    name: 'Skolnit',
    race: 'Human',
    playerName: 'g',
    classes: [{ name: 'Ranger', level: 5, subclass: 'Gloom Stalker', hitDie: 10 }],
    abilityScores: { strength: 10, dexterity: 18, constitution: 15, intelligence: 10, wisdom: 16, charisma: 10 },
    proficiencyBonus: 3,
    armorClass: 16, // Studded Leather (12) + DEX (4)
    maxHp: 44,
    currentHp: 44,
    hitDice: '5d10',
    hitDiceRemaining: 5,
    speed: 30,
    savingThrowProficiencies: ['strength', 'dexterity'],
    skillProficiencies: ['perception', 'stealth', 'sleight of hand', 'survival', 'athletics'],
    equipment: [
      'Longbow +1',
      'Fangblade (shortsword)',
      'Shortsword',
      'Studded Leather Armor',
      'Quiver (20 arrows)',
    ],
    features: [
      { name: 'Dread Ambusher', source: 'Gloom Stalker 3', description: '+10ft speed and extra attack with +1d8 damage on first turn of combat.' },
      { name: 'Umbral Sight', source: 'Gloom Stalker 3', description: '60ft Darkvision. Invisible to creatures relying on darkvision to see you.' },
      { name: 'Extra Attack', source: 'Ranger 5', description: 'Attack twice when you take the Attack action.' },
      { name: 'Two-Weapon Fighting', source: 'Ranger 2', description: 'Add ability modifier to damage of off-hand attack.' },
      { name: 'Favored Foe', source: 'Ranger 1', description: 'Mark a creature, deal +1d6 damage once per turn (concentration).' },
    ],
    spellcasting: {
      ability: 'wisdom',
      spellSlots: { '1': 4, '2': 2 },
      spellSlotsUsed: {},
      knownSpells: ['Cure Wounds', 'Fog Cloud', "Hunter's Mark", 'Speak with Animals', 'Spike Growth', 'Disguise Self'],
      preparedSpells: ["Hunter's Mark", 'Spike Growth', 'Cure Wounds'],
    },
    badgeId: 'moss',
    avatarUrl: '/assets/characters/skolnit.png',
    appearance: 'Lean, wiry human male in his 20s. Tanned skin, short dark hair, sharp watchful eyes. Wears worn studded leather armor with a hooded cloak. Longbow across back, shortsword at hip. Moves like a predator.',
    personalityTraits: 'Street-smart survivor. Trusts instincts over words.',
    ideals: 'Freedom. No one should have to live in fear.',
    bonds: 'Grew up on the streets. Looks out for the little guy.',
    flaws: 'Slow to trust authority figures.',
  }, campaignId);

  // MAD JUJU - The Undead Warlock 5
  // Race: Dhampir | Background: Slave
  // Role: Face / Blaster / Control
  const madJuju = findByName('Mad Juju') || store.addCharacter({
    name: 'Mad Juju',
    race: 'Dhampir',
    playerName: 'g',
    classes: [{ name: 'Warlock', level: 5, subclass: 'The Undead', hitDie: 8 }],
    abilityScores: { strength: 10, dexterity: 13, constitution: 18, intelligence: 10, wisdom: 11, charisma: 18 },
    proficiencyBonus: 3,
    armorClass: 12, // Myceliaweave Armor (cursed leather)
    maxHp: 48,
    currentHp: 48,
    hitDice: '5d8',
    hitDiceRemaining: 5,
    speed: 30,
    savingThrowProficiencies: ['wisdom', 'charisma'],
    skillProficiencies: ['deception', 'intimidation', 'performance', 'stealth', 'survival'],
    equipment: [
      'Myceliaweave Armor (cursed)',
      'Component Pouch',
      'Book of Shadows',
      'Dagger',
    ],
    features: [
      { name: 'Form of Dread', source: 'Undead 1', description: 'Transform as bonus action. Gain temp HP, immunity to frightened, frighten on hit.' },
      { name: 'Grave Touched', source: 'Undead 6', description: "Don't need to eat/drink/breathe. Necrotic damage can replace spell damage types." },
      { name: 'Pact of the Tome', source: 'Warlock 3', description: 'Book of Shadows with 3 extra cantrips from any class.' },
      { name: 'Agonizing Blast', source: 'Invocation', description: 'Add CHA (+4) to Eldritch Blast damage.' },
      { name: "Devil's Sight", source: 'Invocation', description: 'See normally in magical and nonmagical darkness to 120ft.' },
      { name: 'Vampiric Bite', source: 'Dhampir', description: 'CON + Prof/day. 1d4+CON piercing, heal for damage dealt or empower next roll.' },
      { name: 'Spider Climb', source: 'Dhampir', description: 'Climb speed equal to walking speed. Walk on walls and ceilings.' },
    ],
    spellcasting: {
      ability: 'charisma',
      spellSlots: { '3': 2 }, // Warlock pact slots (2 slots, 3rd level)
      spellSlotsUsed: {},
      knownSpells: [
        // Cantrips
        'Eldritch Blast', 'Mind Sliver', 'Chill Touch', 'Guidance', 'Thaumaturgy', 'Infestation',
        // Spells
        'Charm Person', 'Hellish Rebuke', 'Misty Step', 'Darkness', 'Vampiric Touch', 'Bane', 'Blindness/Deafness',
      ],
      preparedSpells: ['Eldritch Blast', 'Misty Step', 'Darkness', 'Vampiric Touch'],
    },
    badgeId: 'amethyst',
    avatarUrl: '/assets/characters/madjuju.png',
    appearance: 'Beautiful black woman, tall and striking with rich dark brown skin. Wild natural black hair decorated with small bones and beads. Warm amber eyes. Wears weathered leather armor with fungal patterns. Carries an old leather-bound book. Louisiana voodoo priestess aesthetic, confident stance, charismatic smirk.',
    personalityTraits: 'Charismatic and unsettling. Embraces the darkness within.',
    ideals: 'Power. Will never be enslaved again.',
    bonds: 'Bound to an undead patron. Seeks to understand the nature of undeath.',
    flaws: 'The hunger is always there. Sometimes gives in to dark impulses.',
  }, campaignId);

  // BOBBY - Echo Knight Fighter 5
  // Full name: Robert "Xthilick" Boucher
  // Race: Custom Lineage (green skin) | Background: Swamp Dweller
  // Role: Frontline / Tank / Melee DPR
  const bobby = findByName('Bobby') || store.addCharacter({
    name: 'Bobby',
    race: 'Custom Lineage',
    playerName: 'g',
    classes: [{ name: 'Fighter', level: 5, subclass: 'Echo Knight', hitDie: 10 }],
    abilityScores: { strength: 16, dexterity: 13, constitution: 16, intelligence: 8, wisdom: 12, charisma: 9 },
    proficiencyBonus: 3,
    armorClass: 17, // Chain Mail (16) + Defense (+1)
    maxHp: 49,
    currentHp: 49,
    hitDice: '5d10',
    hitDiceRemaining: 5,
    speed: 30,
    savingThrowProficiencies: ['strength', 'constitution'],
    skillProficiencies: ['athletics', 'perception', 'survival'],
    equipment: [
      'Glaive +1',
      'Chain Mail',
      'Handaxe (2)',
      'Shield (backup)',
    ],
    features: [
      { name: 'Manifest Echo', source: 'Echo Knight 3', description: 'Summon echo within 15ft. Attack from its space. Swap places as bonus action.' },
      { name: 'Unleash Incarnation', source: 'Echo Knight 3', description: 'CON mod times per long rest, make extra attack from echo when you Attack.' },
      { name: 'Extra Attack', source: 'Fighter 5', description: 'Attack twice when you take the Attack action.' },
      { name: 'Action Surge', source: 'Fighter 2', description: 'Take an additional action on your turn. Recharges on short rest.' },
      { name: 'Second Wind', source: 'Fighter 1', description: 'Bonus action: regain 1d10+5 HP. Recharges on short rest.' },
      { name: 'Sentinel', source: 'Feat', description: 'OA on Disengage. Hit stops movement. OA when ally attacked within 5ft.' },
      { name: 'Great Weapon Master', source: 'Feat', description: '-5 to hit for +10 damage. Bonus action attack on crit or kill.' },
    ],
    badgeId: 'steel',
    avatarUrl: '/assets/characters/bobby.png',
    appearance: 'Stocky, muscular humanoid with green-tinted skin. Square jaw, small eyes, Cajun swamp dweller look. Wears battered chain mail. Carries a massive glaive. An ethereal translucent echo of himself shimmers beside him.',
    personalityTraits: 'Simple and direct. Speaks in a thick Cajun accent.',
    ideals: 'Family. Mama raised him right.',
    bonds: 'From the swamps. Fights to protect those who cannot protect themselves.',
    flaws: "Not the brightest. Sometimes doesn't know his own strength.",
  }, campaignId);

  // DAX - Alchemist Artificer 5
  // Full name: (Former) Assistant Professor Daxion "Dax" Corvallis
  // Race: Variant Human | Background: Archaeologist
  // Role: Support / Utility / Ranged
  const dax = findByName('Dax') || store.addCharacter({
    name: 'Dax',
    race: 'Human (Variant)',
    playerName: 'g',
    classes: [{ name: 'Artificer', level: 5, subclass: 'Alchemist', hitDie: 8 }],
    abilityScores: { strength: 10, dexterity: 13, constitution: 15, intelligence: 17, wisdom: 12, charisma: 8 },
    proficiencyBonus: 3,
    armorClass: 13, // Studded Leather (12) + DEX (1)
    maxHp: 38,
    currentHp: 38,
    hitDice: '5d8',
    hitDiceRemaining: 5,
    speed: 30,
    savingThrowProficiencies: ['constitution', 'intelligence'],
    skillProficiencies: ['arcana', 'history', 'investigation', 'perception', 'survival'],
    equipment: [
      "Alchemist's Supplies",
      "Tinker's Tools",
      'Light Crossbow',
      'Studded Leather Armor',
      'Homunculus Servant',
    ],
    features: [
      { name: 'Experimental Elixir', source: 'Alchemist 3', description: 'Create free elixirs on long rest. Spend slot for more. Random or chosen effects.' },
      { name: 'Alchemical Savant', source: 'Alchemist 5', description: "+INT to one healing or damage roll of artificer spells using alchemist's supplies." },
      { name: 'Homunculus Servant', source: 'Infusion', description: 'Tiny construct companion. Can deliver touch spells, scout, and attack with Force Strike.' },
      { name: 'Infuse Item', source: 'Artificer 2', description: 'Imbue items with magic. 4 infusions known, 2 active at a time.' },
      { name: 'Observant', source: 'Feat', description: '+5 to passive Perception and Investigation. Read lips.' },
      { name: 'Crossbow Expert', source: 'Feat', description: 'Ignore loading. No disadvantage in melee. Bonus action hand crossbow attack.' },
    ],
    spellcasting: {
      ability: 'intelligence',
      spellSlots: { '1': 4, '2': 2 },
      spellSlotsUsed: {},
      knownSpells: [
        // Cantrips
        'Mending', 'Mage Hand',
        // Prepared Spells (INT + half level = 5)
        'Cure Wounds', 'Healing Word', 'Catapult', 'Faerie Fire',
        'Heat Metal', 'Flaming Sphere', 'Lesser Restoration', 'Ray of Sickness',
      ],
      preparedSpells: ['Cure Wounds', 'Healing Word', 'Faerie Fire', 'Heat Metal', 'Flaming Sphere'],
    },
    badgeId: 'ember',
    avatarUrl: '/assets/characters/dax.png',
    appearance: 'Thin, bookish human man with round spectacles and unkempt brown hair. Wears a leather apron over studded armor, pockets full of vials and tools. A tiny mechanical homunculus perches on his shoulder. Light crossbow at his side.',
    personalityTraits: 'Curious academic. Always examining things. Takes meticulous notes.',
    ideals: 'Knowledge. The secrets of the past can unlock the future.',
    bonds: 'Former assistant professor. Seeking lost artifacts and ancient knowledge.',
    flaws: 'Socially awkward. Gets distracted by interesting discoveries.',
  }, campaignId);

  // ASHALA TORUK - Circle of the Land (Coast) Druid 4
  // Full name: Ashala Toruk
  // Race: Human | Background: Outlander
  // Role: Support / Control / Healing
  const ashala = findByName('Ashala') || store.addCharacter({
    name: 'Ashala',
    race: 'Human',
    playerName: 'Ashala',
    classes: [{ name: 'Druid', level: 4, subclass: 'Circle of the Land (Coast)', hitDie: 8 }],
    abilityScores: { strength: 8, dexterity: 12, constitution: 14, intelligence: 13, wisdom: 16, charisma: 10 },
    proficiencyBonus: 2,
    armorClass: 15, // Studded Leather (12) + DEX (1) + Shield (2)
    maxHp: 31,
    currentHp: 31,
    hitDice: '4d8',
    hitDiceRemaining: 4,
    speed: 30,
    savingThrowProficiencies: ['intelligence', 'wisdom'],
    skillProficiencies: ['animal handling', 'medicine', 'nature', 'perception', 'survival'],
    equipment: [
      'Scimitar',
      'Studded Leather Armor',
      'Shield',
      'Druidic Focus',
      'Herbalism Kit',
      'Potion of Healing (2)',
    ],
    features: [
      { name: 'Wild Shape', source: 'Druid 2', description: '2 uses. Transform into CR 1/2 beast without flying speed. Lasts 2 hours.' },
      { name: 'Natural Recovery', source: 'Circle of Land 2', description: 'Short rest: recover spell slots up to half druid level (2 levels worth).' },
      { name: 'Circle Spells (Coast)', source: 'Circle of Land 3', description: 'Always prepared: Mirror Image, Misty Step.' },
      { name: 'Wanderer', source: 'Outlander', description: 'Excellent memory for maps. Can find food/water for 6 people in the wild.' },
      { name: 'Telekinetic', source: 'Feat', description: 'Invisible Mage Hand. Bonus action: shove creature 5ft (DC 13 STR save).' },
    ],
    spellcasting: {
      ability: 'wisdom',
      spellSlots: { '1': 4, '2': 3 },
      spellSlotsUsed: {},
      knownSpells: [
        // Cantrips
        'Druidcraft', 'Produce Flame', 'Guidance',
        // Always Prepared (Circle)
        'Mirror Image', 'Misty Step',
        // Prepared Spells (WIS + level = 7)
        'Cure Wounds', 'Healing Word', 'Entangle', 'Faerie Fire', 'Goodberry',
        'Absorb Elements', 'Ice Knife', 'Thunderwave',
        'Flaming Sphere', 'Moonbeam',
      ],
      preparedSpells: ['Cure Wounds', 'Healing Word', 'Entangle', 'Faerie Fire', 'Moonbeam', 'Flaming Sphere', 'Ice Knife'],
    },
    badgeId: 'verdant',
    avatarUrl: '/assets/characters/ashala.png',
    appearance: 'Chultan human male, dark brown skin, shaved head with ritual scarification patterns. Calm, steady gaze. Wears earth-toned leather with a wooden shield carved with wave motifs. Druidic focus woven into a belt of dried herbs. Scimitar at hip.',
    personalityTraits: 'Quietly observant. Prefers to watch and listen before acting. Pragmatic and level-headed.',
    ideals: 'Balance. The natural world must remain in harmony. Undead must be eradicated.',
    bonds: 'Protector of the Sacred Sites of Ubtao. This duty is central to his identity and tribal legacy.',
    flaws: 'Emotionally detached. His neutral outlook makes him appear indifferent to others\' struggles.',
  }, campaignId);

  // BROTHER SHAGO - Life Domain Cleric 5 (AI-controlled NPC healer)
  // Race: Human (Chultan) | Background: Acolyte
  // Role: Autonomous healer — runs via heuristic AI, never needs player input
  const shago = findByName('Brother Shago') || store.addCharacter({
    name: 'Brother Shago',
    race: 'Human',
    isNpc: true,
    npcControlMode: 'ai',
    npcPersonality: 'Quiet, competent, laconic. Heals without fanfare. Never explains the Shago resemblance.',
    classes: [{ name: 'Cleric', level: 5, subclass: 'Life Domain', hitDie: 8 }],
    abilityScores: { strength: 14, dexterity: 10, constitution: 14, intelligence: 10, wisdom: 18, charisma: 12 },
    proficiencyBonus: 3,
    armorClass: 18, // Chain Mail (16) + Shield (2)
    maxHp: 43,
    currentHp: 43,
    hitDice: '5d8',
    hitDiceRemaining: 5,
    speed: 30,
    savingThrowProficiencies: ['wisdom', 'charisma'],
    skillProficiencies: ['medicine', 'religion', 'insight', 'perception'],
    equipment: [
      'Mace',
      'Chain Mail',
      'Shield',
      'Holy Symbol of Ubtao',
    ],
    features: [
      { name: 'Disciple of Life', source: 'Life Domain 1', description: 'Healing spells restore additional 2 + spell level HP.' },
      { name: 'Preserve Life', source: 'Life Domain 2', description: 'Channel Divinity: restore HP equal to 5x cleric level, divided among creatures within 30ft.' },
      { name: 'Blessed Healer', source: 'Life Domain 6', description: 'When you cast a healing spell on another, you regain 2 + spell level HP.' },
      { name: 'Channel Divinity', source: 'Cleric 2', description: '2 uses per short rest. Turn Undead or Preserve Life.' },
    ],
    spellcasting: {
      ability: 'wisdom',
      spellSlots: { '1': 4, '2': 3, '3': 2 },
      spellSlotsUsed: {},
      knownSpells: [
        // Cantrips
        'Sacred Flame', 'Spare the Dying', 'Guidance',
        // Domain spells (always prepared)
        'Bless', 'Cure Wounds', 'Lesser Restoration', 'Spiritual Weapon',
        'Beacon of Hope', 'Revivify',
        // Prepared spells
        'Healing Word', 'Shield of Faith', 'Aid',
        'Prayer of Healing', 'Spirit Guardians', 'Mass Healing Word',
      ],
      preparedSpells: [
        'Cure Wounds', 'Healing Word', 'Bless', 'Shield of Faith',
        'Spiritual Weapon', 'Aid', 'Prayer of Healing',
        'Spirit Guardians', 'Mass Healing Word', 'Revivify',
      ],
    },
    badgeId: 'ochre',
    badgeIcon: 'sparkle',
    avatarUrl: '/assets/characters/brother_shago.png',
    appearance: 'Black Chultan human male, middle-aged, deep dark skin weathered by sun. Close-cropped grey hair, kind but weary eyes. Wears heavy chain mail with a sun-bleached tabard bearing the symbol of Ubtao. Mace at belt, shield on back. Hands often glow faintly gold with healing magic.',
    background: 'Acolyte',
    alignment: 'Neutral Good',
    personalityTraits: 'Quietly competent. Hands already glowing before you ask.',
    ideals: 'Service. The living need tending. The dead can wait.',
    bonds: 'His shrine was overrun during the death curse. Now he walks with the living.',
    flaws: 'Will not explain the resemblance to Lady E\'s majordomo. Ever.',
  }, campaignId);

  // Add to party in marching order (only if not already in party)
  if (skolnit && !store.partyIds.includes(skolnit.id)) store.addToParty(skolnit.id);  // Point/Scout
  if (bobby && !store.partyIds.includes(bobby.id)) store.addToParty(bobby.id);    // Front line
  if (shago && !store.partyIds.includes(shago.id)) store.addToParty(shago.id);    // Mid (cleric/healer NPC)
  if (ashala && !store.partyIds.includes(ashala.id)) store.addToParty(ashala.id);   // Mid (druid/healer)
  if (madJuju && !store.partyIds.includes(madJuju.id)) store.addToParty(madJuju.id);  // Mid (caster)
  if (dax && !store.partyIds.includes(dax.id)) store.addToParty(dax.id);      // Rear (support)
}
